/**
 * @file splitViewUtils.tsx
 *
 * @fileoverview Util functions for the job split view layout.
 */

import { SAVE_SEARCH_FRAGMENT } from '~/jobiqo/components/JobAlert/context';
import { Job } from '~/jobiqo/types/types';
import Router from 'next/router';

const getCurrentVisiblePath = (fallbackPath: string): string => {
  if (typeof window === 'undefined') {
    return fallbackPath;
  }

  const currentPath = window.location?.pathname;
  if (!currentPath) {
    return fallbackPath;
  }

  // Normalize browser pathname by removing basePath (if any), because Next.js
  // handles basePath automatically during navigation.
  const basePath = Router.basePath || '';
  const normalizedPath =
    basePath && currentPath.startsWith(`${basePath}/`)
      ? currentPath.slice(basePath.length)
      : currentPath;

  return `${normalizedPath}${window.location?.search ?? ''}`;
};

/**
 * Generates the url for a given job detail in a search.
 *
 * Will append the jobId in the url so that when a particular job is
 * clicked the detail of that job can be loaded.
 *
 * @param {Job} job
 *  The job to append.
 *
 * @param {any} querystring
 *  The current querystring
 *
 * @param {string} pathname
 *  The current path
 *
 * @param {string} path
 *  The current path
 *
 * @returns any
 *   The job url object.
 */
export const getJobAppendUrl = (
  job: Job,
  querystring: any,
  pathname: string,
  path: string,
) => {
  const {
    jobId: _oldJobId,
    backfillid: _oldBackfillId,
    taxonomy: _oldTaxonomy,
    router: _oldRouter,
    ...queryWithoutIds
  } = querystring || {};
  const querystringReturn: any = { ...queryWithoutIds };

  if (job.id) {
    querystringReturn.jobId = job.id;
  }
  if ((job as any)?.backfillid) {
    querystringReturn.backfillid = (job as any)?.backfillid;
  }

  // Keep users on the currently visible path and only update job selection params.
  const currentPath = getCurrentVisiblePath(path);
  const [pathWithoutQuery, queryPart = ''] = currentPath.split('?');
  const searchParams = new URLSearchParams(queryPart);
  searchParams.delete('jobId');
  searchParams.delete('backfillid');
  searchParams.delete('taxonomy');
  searchParams.delete('router');
  if (job.id) {
    searchParams.set('jobId', String(job.id));
  }
  if ((job as any)?.backfillid) {
    searchParams.set('backfillid', (job as any)?.backfillid);
  }

  let pathAs = `${pathWithoutQuery}${
    searchParams.toString() ? `?${searchParams.toString()}` : ''
  }`;

  // Remove save search fragment if it exists.
  if (pathAs.includes(SAVE_SEARCH_FRAGMENT)) {
    pathAs = pathAs.replace(SAVE_SEARCH_FRAGMENT, '');
  }

  return {
    route: {
      pathname,
      query: querystringReturn,
    },
    as: pathAs,
  };
};

/**
 * Check if the current job is active.
 *
 * @param {Job} job
 *  The job to check.
 *
 * @param {string | null} currentJobId
 *  The current job ID.
 *
 * @returns {boolean}
 *   True if the job is currently selected.
 */
export const isActiveJob = (job: Job, currentJobId: string | null): boolean => {
  // Handle context-based currentJobId (string | null).
  if (typeof currentJobId === 'string' || currentJobId === null) {
    return currentJobId !== null && Number(currentJobId) === job.id;
  }
  return false;
};
